import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import SEOHead from './SEOHead';
import Header from './Header';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import NewsletterSignup from './NewsletterSignup';
import { pageSEO } from '../utils/seoConfig';
import { getBreadcrumbStructuredData } from '../utils/structuredData';
import { Search, Clock, ArrowRight, Loader2, Sparkles } from 'lucide-react';

interface BlogPost {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  featured_image: string | null;
  category_id: string | null;
  author_name: string;
  author_avatar: string | null;
  reading_time_minutes: number;
  published_at: string;
  is_featured: boolean;
  tags: string[];
}

interface BlogCategory {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
}

const BlogList: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language].blog;
  const seo = pageSEO.blog;

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [postsRes, categoriesRes] = await Promise.all([
        supabase
          .from('blog_posts')
          .select('id, slug, title_es, title_en, excerpt_es, excerpt_en, featured_image, category_id, author_name, author_avatar, reading_time_minutes, published_at, is_featured, tags')
          .eq('is_published', true)
          .order('published_at', { ascending: false }),
        supabase
          .from('blog_categories')
          .select('id, slug, name_es, name_en')
          .order('name_es', { ascending: true })
      ]);

      if (postsRes.data) setPosts(postsRes.data);
      if (categoriesRes.data) setCategories(categoriesRes.data);
      setLoading(false);
    };
    fetchData();
  }, []);

  const featuredPost = useMemo(() => posts.find(p => p.is_featured) || posts[0], [posts]);

  const filteredPosts = useMemo(() => {
    let result = posts;

    if (activeCategory === 'all') {
      result = result.filter(p => !p.is_featured || p.id !== featuredPost?.id);
    } else {
      const cat = categories.find(c => c.slug === activeCategory);
      if (cat) result = result.filter(p => p.category_id === cat.id);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (language === 'es' ? p.title_es : p.title_en).toLowerCase().includes(q) ||
        (language === 'es' ? p.excerpt_es : p.excerpt_en).toLowerCase().includes(q) ||
        p.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }

    return result;
  }, [posts, activeCategory, searchQuery, language, categories, featuredPost]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visiblePosts.length < filteredPosts.length;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryName = (catId: string | null) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return '';
    return language === 'es' ? cat.name_es : cat.name_en;
  };

  const breadcrumb = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx' },
    { name: 'Blog', url: 'https://laimu.mx/blog' }
  ]);

  return (
    <>
      <SEOHead
        title={seo.title[language]}
        description={seo.description[language]}
        keywords={seo.keywords[language]}
        url="/blog"
        type="blog"
        structuredData={breadcrumb}
      />
      <Header />
      <ScrollToTop />

      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #115d7b 0%, #0d4a63 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full blur-3xl" style={{ backgroundColor: '#ff8c00' }}></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#115d7b' }}></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm mb-4">
              <Sparkles className="w-4 h-4 text-orange-400" />
              <span className="text-white/90 text-sm font-medium">LAIMU Blog</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t.heroTitle}</h1>
            <p className="text-lg text-white/80">{t.heroSubtitle}</p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-1 rounded-full"
                style={{ backgroundColor: 'rgba(17, 93, 123, 0.1)', color: '#115d7b' }}>
                <Sparkles className="w-3.5 h-3.5" />
                {t.featuredLabel}
              </span>
            </div>
            <a href={`/blog/${featuredPost.slug}`} className="group block rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 bg-white border border-gray-100">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={featuredPost.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'}
                    alt={language === 'es' ? featuredPost.title_es : featuredPost.title_en}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: '#ff8c00' }}>
                      {getCategoryName(featuredPost.category_id)}
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-10 flex flex-col justify-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                    {language === 'es' ? featuredPost.title_es : featuredPost.title_en}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {language === 'es' ? featuredPost.excerpt_es : featuredPost.excerpt_en}
                  </p>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={featuredPost.author_avatar || `https://ui-avatars.com/api/?name=${featuredPost.author_name}&background=115d7b&color=fff&size=40`}
                      alt={featuredPost.author_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{featuredPost.author_name}</p>
                      <p className="text-xs text-gray-500">{formatDate(featuredPost.published_at)}</p>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400 text-sm ml-auto">
                      <Clock className="w-3.5 h-3.5" />
                      {featuredPost.reading_time_minutes} {t.minutesRead}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all"
                    style={{ color: '#115d7b' }}>
                    {t.readMore}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>
      )}

      {/* Search & Filter Bar */}
      <section className="pb-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(6); }}
                placeholder={t.searchPlaceholder}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setActiveCategory('all'); setVisibleCount(6); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeCategory === 'all'
                    ? 'text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={activeCategory === 'all' ? { backgroundColor: '#115d7b' } : {}}
              >
                {t.allCategories}
              </button>
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  onClick={() => { setActiveCategory(cat.slug); setVisibleCount(6); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={activeCategory === cat.slug ? { backgroundColor: '#115d7b' } : {}}
                >
                  {language === 'es' ? cat.name_es : cat.name_en}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="pb-12">
        <div className="container mx-auto px-4 md:px-6">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#115d7b' }} />
              <span className="ml-3 text-gray-500">{t.loadingPosts}</span>
            </div>
          ) : visiblePosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">{t.noResults}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visiblePosts.map(post => (
                  <a
                    key={post.id}
                    href={`/blog/${post.slug}`}
                    className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-100 hover:-translate-y-1"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1'}
                        alt={language === 'es' ? post.title_es : post.title_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                          style={{ backgroundColor: '#ff8c00' }}>
                          {getCategoryName(post.category_id)}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                        {language === 'es' ? post.title_es : post.title_en}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {language === 'es' ? post.excerpt_es : post.excerpt_en}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=115d7b&color=fff&size=32`}
                            alt={post.author_name}
                            className="w-7 h-7 rounded-full object-cover"
                          />
                          <span className="text-xs text-gray-500">{post.author_name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                          <Clock className="w-3 h-3" />
                          {post.reading_time_minutes} {t.minutesRead}
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-10">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 6)}
                    className="px-8 py-3 rounded-xl font-semibold border-2 transition-all duration-300 hover:shadow-lg"
                    style={{ borderColor: '#115d7b', color: '#115d7b' }}
                  >
                    {t.loadMore}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <NewsletterSignup variant="card" />
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BlogList;
